import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, Coins, ShoppingCart } from 'lucide-react';
import { Button, Card, CardContent, CardHeader, CardTitle, Skeleton } from '@mlm/ui';
import {
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type NotificationRow,
} from '../lib/api';
import { fmtDateTime } from '../lib/format';

/**
 * JM-EX9 dedicated page. Infinite-scrolls via TanStack Query's cursor
 * (`createdAt` of the last item on the page). Clicking an unread row marks
 * it read; the bell + page invalidate together so the unread badge clears
 * immediately.
 */
export function NotificationsPage() {
  const qc = useQueryClient();
  const q = useInfiniteQuery({
    queryKey: ['notifications', 'list'],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => listNotifications({ limit: 25, cursor: pageParam }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const markAll = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
  const markOne = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  const totalUnread = items.filter((n) => !n.readAt).length;

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h1 className="text-h1">Notifications</h1>
        {totalUnread > 0 && (
          <Button
            variant="outline"
            size="sm"
            disabled={markAll.isPending}
            onClick={() => markAll.mutate()}
          >
            Mark all read
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {q.isLoading ? (
            <Skeleton className="h-32" />
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center py-10 text-center text-sm text-muted-foreground">
              <Bell className="mb-2 h-8 w-8 opacity-40" />
              You don&apos;t have any notifications yet.
            </div>
          ) : (
            <ul className="divide-y">
              {items.map((n) => (
                <li
                  key={n.id}
                  className={`flex cursor-pointer items-start gap-3 py-3 ${
                    n.readAt ? 'opacity-70' : ''
                  } hover:bg-muted/30 -mx-3 px-3 rounded-md`}
                  onClick={() => {
                    if (!n.readAt) markOne.mutate(n.id);
                  }}
                >
                  <NotificationIcon row={n} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{n.title}</p>
                    {n.body && <p className="mt-0.5 text-sm text-muted-foreground">{n.body}</p>}
                    <p className="mt-1 text-xs text-muted-foreground">{fmtDateTime(n.createdAt)}</p>
                  </div>
                  {!n.readAt && (
                    <span
                      className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary"
                      aria-label="unread"
                    />
                  )}
                </li>
              ))}
            </ul>
          )}
          {q.hasNextPage && (
            <div className="pt-3 text-center">
              <Button
                variant="outline"
                size="sm"
                disabled={q.isFetchingNextPage}
                onClick={() => q.fetchNextPage()}
              >
                {q.isFetchingNextPage ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function NotificationIcon({ row }: { row: NotificationRow }) {
  if (row.kind === 'earnings') {
    const cur = String((row.metadata as Record<string, unknown>)?.currency ?? 'cash');
    if (cur === 'cash') {
      return (
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-success/10 text-success">
          <Coins className="h-4 w-4" />
        </div>
      );
    }
    return (
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
        <Coins className="h-4 w-4" />
      </div>
    );
  }
  return (
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
      <ShoppingCart className="h-4 w-4" />
    </div>
  );
}
